import { z } from 'zod';

export const JwtPayloadSchema = z.object({
  userId: z.string().optional(),
  id: z.string().optional(),
  org_id: z.string().optional(),
  organizationId: z.string().optional(),
  email: z.string().optional(),
  username: z.string().optional(),
  role: z.string().optional(),
  systemRole: z.string().optional(),
  isServiceProvider: z.boolean().optional(),
  isInstaller: z.boolean().optional(),
  isPlatformAdmin: z.boolean().optional(),
  permissions: z.array(z.string()).optional(),
  exp: z.number().optional(),
  iat: z.number().optional(),
  jti: z.string().optional(),
}).passthrough();

export type JwtPayload = z.infer<typeof JwtPayloadSchema>;

export const AuthTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  expiresAt: z.number().optional(),
  tokenType: z.string().optional(),
}).passthrough();

export type AuthTokensValidated = z.infer<typeof AuthTokensSchema>;

export function normalizeAuthTokens(
  value: unknown,
): {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  tokenType: string;
} | null {
  const result = AuthTokensSchema.safeParse(value);
  if (!result.success) {
    return null;
  }

  const accessToken = result.data.accessToken;
  const refreshToken =
    typeof result.data.refreshToken === "string" ? result.data.refreshToken : "";
  const tokenType =
    typeof result.data.tokenType === "string" && result.data.tokenType.trim().length > 0
      ? result.data.tokenType
      : "Bearer";

  let expiresAt =
    typeof result.data.expiresAt === "number" && Number.isFinite(result.data.expiresAt)
      ? result.data.expiresAt
      : null;

  if (expiresAt === null) {
    try {
      const raw: unknown = JSON.parse(atob(accessToken.split(".")[1]));
      const payload = JwtPayloadSchema.safeParse(raw);
      if (payload.success && typeof payload.data.exp === "number") {
        expiresAt = payload.data.exp * 1000;
      }
    } catch {
      return null;
    }
  }

  if (expiresAt === null) {
    return null;
  }

  return {
    accessToken,
    refreshToken,
    expiresAt,
    tokenType,
  };
}

export const CsrfDataSchema = z.object({
  token: z.string(),
  timestamp: z.number(),
});

export type CsrfData = z.infer<typeof CsrfDataSchema>;
